/** @jsx jsx */
import { Grid, List } from "@cd2/components"
import { jsx } from "@emotion/core"
import React from "react"
import { LabelInfo } from "../Components/LabelInfo"
import { buildRoute, eRoutes } from "../routes"
import { IRecord } from "../types/schema"
import { HoverCustomerLoader } from "./HoverCustomerLoader"
import { HoverResourceLoader } from "./HoverResourceLoader"
import { HoverUserLoader } from "./HoverUserLoader"
import { HoverOrderLoader } from "./HoverOrderLoader"
import { HoverProductLoader } from "./HoverProductLoader"
import { titleize } from "@cd2/tools"

type IRma = IRecord<
  "rmas",
  "identifier" | "assignedTo" | "customer" | "order" | "serialNumber" | "status"
> & {
  items: IRecord<"rma_items", "product" | "quantity">[]
  contact?: IRecord<"contacts", "name" | "email">
}

interface IProps {
  id: string
  initialData?: IRma
  renderInlineLink?: "block" | "inline"
  noLink?: boolean
  inline?: boolean
  resourceName?: string
}

export function HoverRmaLoader({
  id,
  initialData,
  renderInlineLink,
  noLink,
  inline,
  resourceName,
}: IProps) {
  const skeem = window.skeem

  async function loadData() {
    const user = await skeem.fetchOne<IRma>({
      rmas: {
        attributes: [
          "identifier",
          "assignedTo",
          "customer",
          "order",
          { name: "items", attributes: ["product", "quantity"] },
          "serialNumber",
          "status",
          { name: "contact", attributes: ["name", "email"] },
        ],
        filter: { matches: { id } },
      },
    })
    return user
  }

  return (
    <HoverResourceLoader
      reloadKey={id}
      loadData={loadData}
      initialData={initialData}
      viewLink={ticket => buildRoute(eRoutes["/customer-support/rma/:id"], { id: ticket.id })}
      renderInlineLink={renderInlineLink}
      resourceName={resourceName ?? "RMA"}
      renderHoverContentTitle={record => record.identifier}
      renderHoverContent={record => (
        <div>
          <List>
            <LabelInfo labelWidth={80} label="Status" value={titleize(record.status)} inline />
            <LabelInfo labelWidth={80} label="Identifier" value={record.identifier} inline />
            <LabelInfo labelWidth={80} label="Serial Number" value={record.serialNumber} inline />
            {record.customer && (
              <LabelInfo
                labelWidth={80}
                label="Customer"
                value={<HoverCustomerLoader id={record.customer.id} />}
                inline
              />
            )}
            {record.contact && (
              <React.Fragment>
                <LabelInfo
                  labelWidth={80}
                  label="Contact Name"
                  value={record.contact.name}
                  inline
                />
                <LabelInfo
                  labelWidth={80}
                  label="Contact Email"
                  value={record.contact.email}
                  inline
                />
              </React.Fragment>
            )}
            {record.assignedTo && (
              <LabelInfo
                labelWidth={80}
                label="Assigned To"
                value={<HoverUserLoader id={record.assignedTo.id} resourceName="Assigned To" />}
                inline
              />
            )}
            {record.order && (
              <LabelInfo
                labelWidth={80}
                label="Order"
                value={<HoverOrderLoader id={record.order.id} />}
                inline
              />
            )}
            <LabelInfo
              labelWidth={80}
              label="Products"
              value={
                <Grid gap={8}>
                  {record.items.map(item => (
                    <div key={item.id}>
                      {item.product ? (
                        <HoverProductLoader id={item.product.id} />
                      ) : (
                        <span>No product</span>
                      )}{" "}
                      <span> X {item.quantity}</span>
                    </div>
                  ))}
                </Grid>
              }
              inline
            />
          </List>
        </div>
      )}
      renderTarget={record => <span>{record.identifier}</span>}
      inline={inline}
      noLink={noLink}
    />
  )
}
